import React, { useEffect, useState } from 'react';
import type { Campaign, CampaignItem, Disparo } from '../../types';
import { campaignOriginLabels } from '../../utils/campaignUi';
import { CampaignItemsListPanel } from '../CampaignItemsListPanel/CampaignItemsListPanel';
import './CampaignExecutionDrawer.css';

export type ExecutionDrawerTab = 'detalhes' | 'leads';

export interface CampaignExecutionDrawerProps {
  open: boolean;
  campaign: Campaign | undefined;
  items: CampaignItem[];
  disparo: Disparo | undefined;
  /** Label da conta WhatsApp (já resolvido no pai). */
  contaWhatsappLabel: string;
  responsavelLabel: string;
  defaultRemarketingMeses: number | null | undefined;
  onClose: () => void;
}

export const CampaignExecutionDrawer: React.FC<CampaignExecutionDrawerProps> = ({
  open,
  campaign,
  items,
  disparo,
  contaWhatsappLabel,
  responsavelLabel,
  defaultRemarketingMeses,
  onClose,
}) => {
  const [tab, setTab] = useState<ExecutionDrawerTab>('detalhes');

  useEffect(() => {
    if (open) setTab('detalhes');
  }, [open, campaign?.campaignId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const title = campaign?.nome || 'Campanha';
  const total = campaign?.campaignItemCount ?? items.length;
  const pills = campaignOriginLabels(campaign?.origem);
  const remarketingLabel =
    defaultRemarketingMeses != null &&
    Number.isInteger(defaultRemarketingMeses) &&
    defaultRemarketingMeses >= 1
      ? `${defaultRemarketingMeses} meses`
      : 'não definido';

  return (
    <div className="campaign-exec-drawer-overlay" role="presentation" onClick={onClose}>
      <aside
        className="campaign-exec-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-exec-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="campaign-exec-drawer-header">
          <h2 id="campaign-exec-drawer-title">Detalhe da Campanha</h2>
          <button type="button" className="campaign-exec-drawer-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className="campaign-exec-drawer-tabs" role="tablist" aria-label="Secções">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'detalhes'}
            className={`campaign-exec-drawer-tab ${tab === 'detalhes' ? 'active' : ''}`}
            onClick={() => setTab('detalhes')}
          >
            Detalhes
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'leads'}
            className={`campaign-exec-drawer-tab ${tab === 'leads' ? 'active' : ''}`}
            onClick={() => setTab('leads')}
          >
            Leads da Campanha ({items.length})
          </button>
        </div>

        <div className="campaign-exec-drawer-body">
          {tab === 'detalhes' && (
            <div className="campaign-exec-drawer-detalhes">
              <div className="campaign-exec-drawer-camp-head">
                <h3 className="campaign-exec-drawer-camp-title">{title}</h3>
                {campaign?.status && (
                  <span className={`campaign-exec-drawer-status status-${campaign.status}`}>
                    {campaign.status === 'ativa'
                      ? 'Ativa'
                      : campaign.status === 'rascunho'
                        ? 'Rascunho'
                        : 'Encerrada'}
                  </span>
                )}
              </div>

              <section className="campaign-exec-drawer-section">
                <h4 className="campaign-exec-drawer-label">Mensagem</h4>
                <div className="campaign-exec-drawer-msg-box">
                  {campaign?.mensagem?.trim() ? campaign.mensagem : '—'}
                </div>
              </section>

              <section className="campaign-exec-drawer-metrics">
                <div className="campaign-exec-drawer-metric">
                  <span className="campaign-exec-drawer-metric-label">Total de leads</span>
                  <span className="campaign-exec-drawer-metric-value">{total}</span>
                </div>
                <div className="campaign-exec-drawer-metric">
                  <span className="campaign-exec-drawer-metric-label">Fontes</span>
                  <div className="campaign-exec-drawer-pills">
                    {pills.length === 0 ? (
                      <span className="campaign-exec-drawer-muted">—</span>
                    ) : (
                      pills.map((p) => (
                        <span key={p} className="campaign-exec-drawer-pill">
                          {p}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </section>

              <section className="campaign-exec-drawer-section">
                <h4 className="campaign-exec-drawer-label">Configurações de disparo</h4>
                <dl className="campaign-exec-drawer-dl">
                  <div>
                    <dt>Conta WhatsApp</dt>
                    <dd>{contaWhatsappLabel}</dd>
                  </div>
                  {disparo && (
                    <>
                      <div>
                        <dt>Status</dt>
                        <dd>
                          {disparo.status === 'em_execucao' && 'Em execução'}
                          {disparo.status === 'pausado' && 'Pausado'}
                          {disparo.status === 'pausado_horario' && (
                            <span className="campaign-exec-drawer-badge campaign-exec-drawer-badge--horario">
                              Fora de horário
                            </span>
                          )}
                          {disparo.status === 'max_atingido' && (
                            <span className="campaign-exec-drawer-badge campaign-exec-drawer-badge--max">
                              Máx. atingido
                            </span>
                          )}
                          {disparo.status === 'criado' && 'Aguardando início'}
                          {disparo.status === 'concluido' && 'Concluído'}
                          {disparo.status === 'cancelado' && 'Cancelado'}
                        </dd>
                      </div>
                      <div>
                        <dt>Intervalo entre envios</dt>
                        <dd>
                          {disparo.intervaloMinutos != null
                            ? disparo.intervaloMinutos === 1
                              ? '1 min (~20–60 s)'
                              : `${disparo.intervaloMinutos} min`
                            : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt>Horário</dt>
                        <dd>
                          {(disparo.horaInicio || '').trim() || '—'} – {(disparo.horaFim || '').trim() || '—'}
                        </dd>
                      </div>
                      <div>
                        <dt>Pausar após (envios/rodada)</dt>
                        <dd>
                          {disparo.maxDisparosPorNumero ?? '—'}
                          {disparo.status === 'max_atingido' && (
                            <span className="campaign-exec-drawer-badge campaign-exec-drawer-badge--max" style={{ marginLeft: 6 }}>
                              limite atingido
                            </span>
                          )}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              </section>

              <section className="campaign-exec-drawer-section">
                <h4 className="campaign-exec-drawer-label">Responsáveis</h4>
                <p className="campaign-exec-drawer-plain">{responsavelLabel}</p>
              </section>

              {campaign?.unsubscribeFooterTemplate && (
                <section className="campaign-exec-drawer-section">
                  <h4 className="campaign-exec-drawer-label">Texto de unsubscribe</h4>
                  <div className="campaign-exec-drawer-msg-box">{campaign.unsubscribeFooterTemplate}</div>
                </section>
              )}

              <section className="campaign-exec-drawer-section campaign-exec-drawer-rules">
                <h4 className="campaign-exec-drawer-label">Regras automáticas</h4>
                <p>
                  <strong>Prazo para remarketing:</strong> {remarketingLabel}
                </p>
                <p className="campaign-exec-drawer-muted-small">
                  Configurado pelo administrador. Leads sem sucesso poderão ser incluídos em novas campanhas após este
                  período da última tentativa de contacto.
                </p>
              </section>

              {campaign?.createdAt && (
                <footer className="campaign-exec-drawer-footer">
                  Criada em: {new Date(campaign.createdAt).toLocaleString('pt-BR')}
                </footer>
              )}
            </div>
          )}

          {tab === 'leads' && (
            <div className="campaign-exec-drawer-leads">
              <div className="campaign-exec-drawer-leads-head">
                <span>Leads desta campanha</span>
                <strong>{items.length} leads</strong>
              </div>
              <CampaignItemsListPanel
                items={items}
                showRemarketingHint={false}
                className="campaign-exec-drawer-leads-panel"
              />
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};
